import type { InitialDriversOpenParams } from "../../models/UtilFunctions.model";
import { normalizeToKnownDrivers } from "./Drivers";
import { useEffect, useRef } from "react";

/**
 * On first load per voyage, open drivers that have data from backend (addDrivers)
 * or from local Drivers store snapshot.
 */
export function useInitialDriversOpen({
  tcCode,
  voyageNo,
  isApprovalMode,
  allDrivers,
  addDrivers,
  hasStoreSnapshot,
  getDriversFromStore,
  syncSequenceToUsed,
}: InitialDriversOpenParams) {
  const isFirstLoad = useRef(true);

  useEffect(() => {
    // Reset gate on voyage change
    isFirstLoad.current = true;
  }, [tcCode, voyageNo]);

  useEffect(() => {
    if (!isFirstLoad.current) return;

    const fromBackend = addDrivers?.map((d) => d.driver) ?? [];
    const snapshotExists = !isApprovalMode && hasStoreSnapshot(tcCode, voyageNo);

    const fromStore = snapshotExists ? getDriversFromStore(tcCode, voyageNo) : [];

    const usedDriversOnLoad = isApprovalMode
      ? normalizeToKnownDrivers(fromBackend, allDrivers)
      : snapshotExists
        ? normalizeToKnownDrivers(fromStore, allDrivers)
        : normalizeToKnownDrivers(fromBackend, allDrivers);

    if (usedDriversOnLoad.length === 0) {
      if (snapshotExists) {
        syncSequenceToUsed([], true, { allowMutations: false });
        isFirstLoad.current = false;
      }
      return;
    }

    syncSequenceToUsed(usedDriversOnLoad, true, { allowMutations: !isApprovalMode });
    isFirstLoad.current = false;
  }, [
    tcCode,
    voyageNo,
    isApprovalMode,
    addDrivers,
    allDrivers,
    hasStoreSnapshot,
    getDriversFromStore,
    syncSequenceToUsed,
  ]);
}
